import type {
  AcceleratorReport,
  SpeedBoostRecheck,
  WorkstationHealthReport,
} from '../shared/desktop-api';
import { driverAdvice } from '../shared/driver-advice';

export const speedBoostHardwareNote =
  'Automatically enabled when you have a high-end graphics card with enough available memory.';

const features: Record<string, { title: string; description: string }> = {
  extraction: {
    title: 'Document details',
    description: 'Finds document types, fields, and line items.',
  },
  ocr: { title: 'Searchable text', description: 'Recognizes the words on your pages.' },
  barcode: { title: 'Barcodes', description: 'Reads barcode values when your job uses them.' },
  rasterization: { title: 'Page previews', description: 'Prepares your documents for viewing.' },
};
const memoryShortage = (reason: string) =>
  /not enough for one reader slot|not enough available (?:graphics )?memory|not enough graphics memory|graphics memory was unavailable|graphics memory was in use/i.test(
    reason,
  );
// The engine's reason, or the customer projection's note that replaces it (Performance reads the projection).
const cardKeptForReader = (reason: string) =>
  /graphics card is kept for understanding documents|graphics card is busy with document details/i.test(
    reason,
  );
const isIdle = (component: AcceleratorReport) =>
  component.verdict === 'unavailable' && component.actualDevice === 'Idle';
const missing = (component: AcceleratorReport) =>
  component.verdict === 'unavailable' && /not installed|missing/i.test(component.actualDevice);
const missingRequiredSoftware = (component: AcceleratorReport) =>
  missing(component) || component.speedBoost?.reason === 'Required software is not available.';
const missingBoostSoftware = (reason: string) =>
  /built without graphics support|graphics (?:runtime|software).*(?:missing|not installed)|graphics version.*not installed|software needed for the speed boost is missing/i.test(
    reason,
  );
// The engine's reason first; else the remembered result's, which keeps the advice while the reader is idle.
const driverAdviceFor = (component: AcceleratorReport) =>
  driverAdvice(component.reason ?? '') ??
  driverAdvice(component.speedBoost?.reason ?? component.speedBoostFailureReason ?? '');
const keepWorking = 'You can keep working without the extra speed.';
const memoryRecovery =
  'Close other apps using the graphics card, then choose Computer check to try again. Paige also tries again by itself every few minutes.';
const unknownRecovery =
  'Finish active work and restart Paige to try again. If Speed boost stays off, contact support.';

export function speedBoostState(component: AcceleratorReport) {
  if (component.speedBoost) return component.speedBoost.state;
  if (component.verdict === 'verified-gpu') return 'on';
  if (component.verdict === 'by-design-cpu') return 'off';
  if (
    component.verdict === 'running-on-cpu' ||
    component.speedBoostFailureReason ||
    missing(component)
  )
    return 'problem';
  return component.speedBoostVerifiedBeforeIdle ? 'on' : 'unknown';
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function speedBoostSummary(report: WorkstationHealthReport | null) {
  const components = ['extraction', 'ocr'].map((id) =>
    report?.components.find((c) => c.component === id),
  );
  // Reading pages is off only because the whole card went to understanding
  // documents: the card is fully in use and nothing needs attention, so it counts
  // as on. Otherwise a 12 GB workstation shows a permanent "Partly on" light.
  const states = components.map((c) =>
    !c
      ? 'unknown'
      : speedBoostState(c) === 'off' && cardKeptForReader(c.speedBoost?.reason ?? c.reason ?? '')
        ? 'on'
        : speedBoostState(c),
  );
  const on = states.filter((s) => s === 'on').length;
  const pending = states.includes('unknown'),
    problem = states.includes('problem');
  const mode: 'on' | 'partial' | 'attention' | 'unknown' | 'off' =
    on === 2
      ? 'on'
      : on > 0 && !pending
        ? 'partial'
        : problem
          ? 'attention'
          : pending
            ? 'unknown'
            : 'off';
  const label =
    mode === 'on'
      ? 'On'
      : mode === 'partial'
        ? 'Partly on'
        : mode === 'attention'
          ? 'Needs attention'
          : mode === 'off'
            ? 'Off'
            : 'Not checked';
  const detail =
    components
      .filter((c): c is AcceleratorReport => Boolean(c))
      .map((c) => `${computerFeature(c).title}: ${computerFeature(c).label.toLowerCase()}.`)
      .join(' ') || 'Speed boost has not been checked yet.';
  return { mode, label, detail };
}

/**
 * The graphics cards Speed boost's reading runs on, as the runtimes loaded them (the engine's
 * own rule: name the device that did the work, never a card that sat idle beside it).
 */
export function speedBoostCards(report: WorkstationHealthReport | null): string[] {
  // Only a part the engine verified on a card names a device. A part still counted as on while it
  // is paused (its OCR lanes stopped while the reader holds the card) reports "Still starting" or
  // "Idle" there, a status, which Settings showed as a second card ("Still starting · Intel Arc
  // A770 Graphics", October 7, 2026). The caller falls back to the adapters the check found.
  return [
    ...new Set(
      (report?.components ?? [])
        .filter(
          (component) =>
            (component.component === 'extraction' || component.component === 'ocr') &&
            speedBoostState(component) === 'on' &&
            component.verdict === 'verified-gpu',
        )
        .map((component) => component.actualDevice?.trim() ?? '')
        .filter((device) => device && !/^(processor|cpu|idle)$/i.test(device)),
    ),
  ];
}

/** Speed boost is off for some reading because not enough graphics memory was free when the reader started. */
export function speedBoostMemoryShort(report: WorkstationHealthReport | null): boolean {
  return (report?.components ?? []).some(
    (component) =>
      (component.component === 'extraction' || component.component === 'ocr') &&
      speedBoostState(component) === 'problem' &&
      memoryShortage(
        component.speedBoost?.reason ?? component.speedBoostFailureReason ?? component.reason ?? '',
      ),
  );
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function computerFeature(component: AcceleratorReport) {
  const feature = features[component.component] ?? { title: component.title, description: '' };
  const reason =
    component.speedBoost?.reason ?? component.speedBoostFailureReason ?? component.reason ?? '';
  const state = speedBoostState(component);
  if (state === 'on') return { ...feature, label: 'Speed boost on', tone: 'ready', note: null };
  if (state === 'off' && (component.component === 'extraction' || component.component === 'ocr'))
    return {
      ...feature,
      label: 'Speed boost off',
      tone: 'neutral',
      note: cardKeptForReader(reason)
        ? 'The graphics card is busy with document details.'
        : driverAdviceFor(component),
    };
  if (missingRequiredSoftware(component))
    return {
      ...feature,
      label: 'Needs attention',
      tone: 'attention',
      note: 'Required software is not available.',
    };
  if (state === 'problem')
    return {
      ...feature,
      label: memoryShortage(reason) ? 'Not enough graphics memory' : 'Speed boost unavailable',
      tone: 'attention',
      note: memoryShortage(reason)
        ? 'Not enough graphics memory was free when the document reader started.'
        : (driverAdviceFor(component) ??
          (missingBoostSoftware(reason)
            ? 'The software needed for the speed boost is missing.'
            : 'Paige could not establish why Speed boost is off.')),
    };
  if (state === 'unknown')
    return {
      ...feature,
      label: isIdle(component) ? 'Speed boost not checked yet' : 'Not checked yet',
      tone: 'neutral',
      note: 'Speed boost has not been verified for this configuration yet.',
    };
  return {
    ...feature,
    label: component.verdict === 'verified-gpu' ? 'Speed boost on' : 'Ready',
    tone: 'ready',
    note: null,
  };
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function computerCheckMessage(report: WorkstationHealthReport | null) {
  const components = report?.components ?? [];
  const unread = report?.modelReachability?.unreachableDocuments ?? 0;
  const pending = components.some((component) => speedBoostState(component) === 'unknown');
  const cpu = components.filter((component) => speedBoostState(component) === 'problem');
  const gpu = components.some((component) => speedBoostState(component) === 'on');
  const memory = cpu.some((component) =>
    memoryShortage(
      component.speedBoost?.reason ?? component.speedBoostFailureReason ?? component.reason ?? '',
    ),
  );
  const driver = cpu.map(driverAdviceFor).find(Boolean) ?? null;
  if (unread > 0)
    return {
      title: 'Some documents could not be read',
      detail: `${unread} of ${report!.modelReachability!.documentsAttempted} documents could not be read in the last run. Their information may be missing.`,
      action:
        'Check the affected documents and retry processing. If this continues, contact support.',
      tone: 'attention',
    };
  if (!report || components.length === 0)
    return {
      title: 'Checking your computer…',
      detail: 'Checking whether Paige is ready and its speed boost is working…',
      action: null,
      tone: 'checking',
    };
  if (components.some(missingRequiredSoftware))
    return {
      title: 'Document processing needs attention',
      detail: 'Some of the software Paige needs is not available.',
      action: 'Contact support if this continues.',
      tone: 'attention',
    };
  // Optional: documents are still read, only slower. Computer check keeps its warning
  // color; the workspace banner shows it calmly and briefly (owner, 2026-10-04: red, with
  // "lots of words", for something optional made the tour look broken).
  if (cpu.length > 0)
    return {
      title: memory ? 'Speed boost needs more graphics memory' : 'Speed boost unavailable',
      detail: memory
        ? `Not enough graphics memory was free when the document reader started. ${keepWorking}`
        : driver
          ? `The graphics card's display driver is missing or did not respond. ${keepWorking}`
          : `Paige could not start Speed boost for some work. ${keepWorking}`,
      action: memory ? memoryRecovery : (driver ?? unknownRecovery),
      tone: 'attention',
      optional: true,
      brief: keepWorking,
    };
  if (pending)
    return {
      title: 'Some results are not checked yet',
      detail: 'Paige will check the remaining capabilities when it reads documents.',
      action: 'Add documents or run a performance test to check.',
      tone: 'checking',
    };
  return {
    title: 'Ready to process documents',
    detail: gpu
      ? 'Speed boost is on to help process documents faster.'
      : 'Everything checked is ready.',
    action: null,
    tone: 'ready',
  };
}

const gigabytes = (megabytes: number) =>
  `${(megabytes / 1024).toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} GB`;

/**
 * What a computer check found, in plain lines: the graphics card, its free memory right
 * now, and whether the speed boost is on and why not (owner, 2026-09-29: "Computer check"
 * took him to a tab that told him nothing useful).
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function speedBoostRecheckSummary(recheck: SpeedBoostRecheck) {
  const cards = (recheck.report.graphicsCards ?? []).filter(Boolean);
  const summary = speedBoostSummary(recheck.report);
  const on = summary.mode === 'on' || recheck.outcome === 'on';
  const memory = recheck.freeGraphicsMemoryMegabytes;
  // A card without its driver: trying again cannot help (its memory cannot even be read), so the fix stays the answer.
  const driver =
    recheck.report.components
      .filter((component) => component.component === 'extraction' || component.component === 'ocr')
      .map(driverAdviceFor)
      .find(Boolean) ?? null;
  const why = on
    ? null
    : driver && recheck.outcome !== 'next-document' && recheck.outcome !== 'busy'
      ? driver
      : recheck.outcome === 'next-document'
        ? 'It starts with the next document Paige reads.'
        : recheck.outcome === 'busy'
          ? 'Documents are being read right now; it switches on as soon as they finish.'
          : recheck.outcome === 'not-enough-graphics-memory'
            ? 'Not enough free graphics memory for the document reader. Close other apps using the graphics card, then try again.'
            : recheck.outcome === 'graphics-memory-not-measured'
              ? cards.length
                ? "The graphics card's memory could not be measured."
                : 'This computer has no supported graphics card.'
              : recheck.outcome === 'still-processor'
                ? 'The graphics card could not start the document reader. If this continues, contact support.'
                : summary.mode === 'partial'
                  ? summary.detail
                  : 'The speed boost could not be checked. Try again, or contact support if this continues.';
  const ready = on || recheck.outcome === 'next-document';
  return {
    title: on
      ? 'Speed boost is on'
      : ready
        ? 'Speed boost is ready'
        : summary.mode === 'partial'
          ? 'Speed boost is partly on'
          : 'Speed boost is off',
    tone: ready ? 'ready' : 'attention',
    lines: [
      cards.length ? `Graphics card: ${cards.join(', ')}` : 'Graphics card: none found',
      memory === null
        ? 'Free graphics memory: could not be measured'
        : `Free graphics memory: ${gigabytes(memory)}`,
      on
        ? 'Speed boost: on'
        : `Speed boost: ${ready ? 'ready' : summary.mode === 'partial' ? 'partly on' : 'off'}. ${why}`,
    ],
    canRetry: !on,
  };
}

/** Native diagnostics stay internal. Only known meanings enter visible and copied reports. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function customerHealthReport(report: WorkstationHealthReport): WorkstationHealthReport {
  const message = computerCheckMessage(report);
  const components = report.components.map((component) => {
    const feature = computerFeature(component);
    return {
      ...component,
      title: feature.title,
      evidence: [],
      speedBoostFailureReason: undefined,
      speedBoost: component.speedBoost
        ? { ...component.speedBoost, reason: feature.note }
        : undefined,
      reason:
        component.verdict === 'running-on-cpu' && memoryShortage(component.reason ?? '')
          ? 'Graphics memory was in use at startup; this is not a current memory reading.'
          : component.verdict === 'running-on-cpu' && driverAdviceFor(component)
            ? driverAdviceFor(component)
            : component.verdict === 'running-on-cpu' && missingBoostSoftware(component.reason ?? '')
              ? 'The software needed for the speed boost is missing.'
              : component.verdict === 'running-on-cpu'
                ? 'This work is using the processor. The cause of the unavailable speed boost was not established.'
                : feature.note,
      cost: null,
      remedy: null,
      lastMeasuredRate: component.lastMeasuredRate
        ? { ...component.lastMeasuredRate, source: 'Previous completed work' }
        : null,
    };
  });
  const reachability = report.modelReachability
    ? { ...report.modelReachability, firstReason: null }
    : null;
  return {
    ...report,
    summary: message.detail,
    components,
    modelReachability: reachability,
    diagnostic: [
      'Paige computer check',
      `Checked: ${report.checkedAt}`,
      `Version: ${report.engineVersion}`,
      message.title,
      message.detail,
      ...components.map(
        (component) =>
          `${component.title}: ${computerFeature(component).label}; ${component.actualDevice}${component.reason ? `; ${component.reason}` : ''}${component.lastMeasuredRate ? `; ${component.lastMeasuredRate.pagesPerMinute.toFixed(1)} pages/minute over ${component.lastMeasuredRate.pages} pages (${component.lastMeasuredRate.measuredAt}, previous completed work)` : ''}`,
      ),
      ...(reachability
        ? [
            `Documents not read: ${reachability.unreachableDocuments} of ${reachability.documentsAttempted}`,
          ]
        : []),
    ].join('\n'),
  };
}
